import { Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ReactComponent as EmojiExtremelyDissatisfied } from "../../../assets/emoji-pouting-face.svg";
import { ReactComponent as EmojiSomewhatDissatisfied } from "../../../assets/emoji-loudly-crying-face.svg";
import { ReactComponent as EmojiNeutral } from "../../../assets/emoji-neutral-face.svg";
import { ReactComponent as EmojiSomewhatSatisfied } from "../../../assets/emoji-slightly-smiling-face.svg";
import { ReactComponent as EmojiExtremelySatisfied } from "../../../assets/emoji-smiling-face-with-heart-eyes.svg";
import { useState } from "react";
import { ButtonZoom } from "../../v2/utils/ButtonZoom";
import { Class, DbUser } from "../../../interfaces";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { Role } from "../../../enums";
import { CourseMetadata } from "@backend-types";
import { Course } from "../../utils/CourseTree";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { generateGtmEvent, getBrowserName, getDeviceLabel } from "../../../Helpers";

interface RateDigitProps {
  currentUser: DbUser;
  assetId: string;
  assetType: string;
  courseMetadata?: CourseMetadata;
  currentCourse: Course | null;
  currentClass?: Class;
}

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: "8px",
    position: "relative",
    border: `2px solid ${theme.palette.neutral.main}`,
    backgroundColor: "#FFF",
    transition: "height 0.2s ease-in-out",
    overflow: "hidden",
  },
  default: {
    height: "178px",
  },
  expanded: {
    height: "228px",
  },
  submitted: {
    height: "auto",
  },
  content: {
    padding: "24px",
  },
  emojiSelected: {
    backgroundColor: "#F2F2F2",
  },
  emojiDefault: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#F2F2F2",
    },
    "&:active": {
      backgroundColor: "#F2F2F2",
    },
    "&:focus": {
      backgroundColor: "#F2F2F2",
    },
  },
  emojiContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "88px",
    height: "92px",
    borderRadius: "4px",
    padding: "8px",
    cursor: "pointer",
  },
  emoji: {
    fontSize: "40px",
    lineHeight: "40px",
    height: "40px",
  },
  buttonZoom: {
    padding: "16px",
    height: "48px",
    borderRadius: "8px",
  },
  backButton: {
    backgroundColor: "transparent",
    borderRadius: "4px",
    color: theme.palette.text.secondary,
    width: "auto",
    height: "28px",
    fontSize: "16px",
    padding: "0px",
    marginRight: "auto",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      color: theme.palette.text.primary,
    },
  },
}));

interface IRating {
  emoji: JSX.Element;
  text: string;
  id: string;
}

type TScreen = "default" | "expanded" | "submitted";
type TFeedback = "positive" | "negative";

export const RateDigit = (props: RateDigitProps) => {
  const { courseMetadata, currentCourse, currentClass } = props;
  const classes = useStyles();
  const [screen, setScreen] = useState<TScreen>("default");
  const [ratingSelected, setRatingSelected] = useState<IRating | null>(null);
  const [ratingReason, setRatingReason] = useState<string>("");
  const [feedbackType, setFeedbackType] = useState<TFeedback>("positive");
  const [sheetRowNumber, setSheetRowNumber] = useState<number | null>(null);

  const ratingUrl =
    courseMetadata?.learning_experience?.rate_digit_url ??
    process.env.REACT_APP_RATING_URL;

  const ratingsStyle = { width: "auto", height: "40px" };
  const ratings: IRating[] = [
    {
      emoji: <EmojiExtremelyDissatisfied style={ratingsStyle} />,
      text: "Extremely dissatisfied",
      id: "extremely-dissatisfied",
    },
    {
      emoji: <EmojiSomewhatDissatisfied style={ratingsStyle} />,
      text: "Somewhat dissatisfied",
      id: "somewhat-dissatisfied",
    },
    {
      emoji: <EmojiNeutral style={ratingsStyle} />,
      text: "Neutral Feeling",
      id: "neutral-feeling",
    },
    {
      emoji: <EmojiSomewhatSatisfied style={ratingsStyle} />,
      text: "Somewhat satisfied",
      id: "somewhat-satisfied",
    },
    {
      emoji: <EmojiExtremelySatisfied style={ratingsStyle} />,
      text: "Extremely satisfied",
      id: "extremely-satisfied",
    },
  ];

  const handleBack = () => {
    setScreen("default");
  };

  const handleCancel = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "why did you rate it this way? your feedback is anonymous. (optional)",
      text: "cancel",
    });
    setScreen("default");
    setRatingSelected(null);
  };

  const sendFeedback = async (
    rating: IRating | null,
    feedback: string,
    rowNumber: number | null
  ) => {
    if (!ratingUrl) return;

    try {
      let canvas_term_name: string = "";
      let canvas_course_id: string = "";
      let canvas_section_id: string = "";
      let canvas_section_name: string = "";
      const currentClassId =
        MacsAuthenticator.authHook.studentCurrentClassId !== null
          ? Number(MacsAuthenticator.authHook.studentCurrentClassId)
          : null;

      if (currentClassId !== null) {
        const currentStudentClass = props.currentUser.class_student.find(
          (cls) => cls.class_id === currentClassId
        );

        const classMetadata = currentStudentClass?.class?.metadata as {
          canvas_term_name?: string;
        };
        canvas_term_name = classMetadata?.canvas_term_name ?? "";
        canvas_course_id = currentStudentClass?.class?.external_parent_id ?? "";
        canvas_section_id = currentStudentClass?.class?.external_id ?? "";
        canvas_section_name = currentStudentClass?.class?.period_block ?? "";
      }
      const formData = new FormData();
      formData.append("ratings", rating?.text || "");
      formData.append("feedback", feedback);
      formData.append("role", props.currentUser.role);
      formData.append(
        "isTester",
        props.currentUser.role !== Role.student ||
          props.currentUser.is_tester ||
          props.currentUser.is_tester_class
          ? "TRUE"
          : "FALSE"
      );
      formData.append("assetId", props.assetId);
      formData.append("assetType", props.assetType);
      formData.append("environment", process.env.REACT_APP_ENV || "development");
      const metadata = currentClass?.is_demo
        ? `${currentCourse?.name} DEMO`
        : `${currentCourse?.name}`;
      formData.append("additional_metadata", metadata);
      formData.append("canvas_term_name", canvas_term_name);
      formData.append("canvas_course_id", canvas_course_id);
      formData.append("canvas_section_id", canvas_section_id);
      formData.append("canvas_section_name", canvas_section_name);
      formData.append("browser_name", getBrowserName());
      formData.append("device_type", getDeviceLabel());

      if (rowNumber !== null) {
        formData.append("rowNumber", rowNumber.toString());
      }

      // Initial POST request
      let response = await axios.post(ratingUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        maxRedirects: 0, // Prevent Axios from following redirects automatically
        validateStatus: (status) => status >= 200 && status < 400, // Consider 302 as valid response to handle manually
      });

      // Check if response indicates a redirect
      while (response.status === 302 && response.headers.location) {
        const redirectedUrl = response.headers.location;

        // Follow the redirect
        response = await axios.get(redirectedUrl, {
          maxRedirects: 0, // Prevent automatic redirect following
          validateStatus: (status) => status >= 200 && status < 400,
        });
      }

      return response.data; // Final response data after handling redirects
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };

  // Function to handle the initial rating submission
  const handleSelectRating = async (rating: IRating) => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "rating",
      region: "main content",
      section: "rate digit",
      text: rating.text.toLowerCase(), // ensure first character is lowercase for consistency
    });
    setRatingSelected(rating);
    setScreen("expanded");

    if (
      ["extremely-dissatisfied", "somewhat-dissatisfied", "neutral-feeling"].includes(
        rating.id
      )
    ) {
      setFeedbackType("negative");
    }
    const response = await sendFeedback(rating, "", sheetRowNumber);
    if (response && response.rowNumber) {
      setSheetRowNumber(response.rowNumber); // Store the row number for future submissions
    }
  };

  // Function to handle the final form submission
  const handleSubmit = async () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "why did you rate it this way? your feedback is anonymous. (optional)",
      text: "submit",
    });
    setScreen("submitted");
    await sendFeedback(ratingSelected, ratingReason, sheetRowNumber);
  };

  return (
    <Box className={`${classes.root} ${classes[screen]}`}>
      <Box className={classes.content}>
        {screen === "default" && (
          <>
            <Typography
              id="rate-digit"
              variant="h4"
              fontSize="24px"
              lineHeight="26px"
              fontWeight={600}
              sx={{ marginBottom: "12px" }}>
              Rate Digit
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing="16px"
              flexWrap="nowrap"
              aria-labelledby="rate-digit">
              {ratings.map((rating) => {
                return (
                  <EmojiRating
                    id={rating.id}
                    selected={rating.text === ratingSelected?.text}
                    emoji={rating.emoji}
                    text={rating.text}
                    onClick={() => handleSelectRating(rating)}
                  />
                );
              })}
            </Stack>
          </>
        )}
        {screen === "expanded" && (
          <Stack direction="column">
            <Button
              size="medium"
              className={classes.backButton}
              sx={{ marginBottom: "12px" }}
              variant="contained"
              onClick={handleBack}
              startIcon={<ArrowBackIcon style={{ width: "16px", height: "16px" }} />}>
              Back
            </Button>
            <Typography
              variant="body1"
              lineHeight="16px"
              fontWeight={400}
              sx={{ marginBottom: "12px" }}>
              Why did you rate it this way? Your feedback is anonymous. (Optional)
            </Typography>
            <TextField
              sx={{ marginBottom: "3px" }}
              id="rating-why-feedback"
              variant="outlined"
              value={ratingReason}
              fullWidth
              onChange={(e) => setRatingReason(e.target.value)}
            />
            <Stack direction="row" sx={{ marginTop: "4px" }}>
              <ButtonZoom
                size="medium"
                variant="outlined"
                className={classes.buttonZoom}
                sx={{ marginRight: "12px" }}
                onClick={handleCancel}>
                Cancel
              </ButtonZoom>
              <ButtonZoom
                size="medium"
                variant="contained"
                className={classes.buttonZoom}
                onClick={handleSubmit}>
                Submit
              </ButtonZoom>
            </Stack>
          </Stack>
        )}
        {screen === "submitted" && (
          <>
            <Typography
              variant="h4"
              fontSize="24px"
              lineHeight="26px"
              fontWeight={600}
              sx={{ marginBottom: "24px" }}>
              Thank you!
            </Typography>

            {feedbackType === "positive" ? (
              <Typography variant="body2" lineHeight="24px" fontWeight={500}>
                Thank you for sharing your thoughts. We appreciate your feedback.
              </Typography>
            ) : (
              <Typography variant="body2" lineHeight="24px" fontWeight={500}>
                <Typography component="span" color="#191919" fontWeight="bold">
                  Thanks for your feedback – we really appreciate it.
                </Typography>{" "}
                If you’d like us to follow up, please{" "}
                <Link
                  href="https://asudev.jira.com/servicedesk/customer/portal/31/group/72/create/296"
                  target="_blank"
                  sx={{ color: "#007CBF" }}>
                  fill out this quick form
                </Link>
                . We’d love your help in making Digit better.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

interface EmojiRatingProp extends IRating {
  onClick: () => void;
  selected: boolean;
}

const EmojiRating = (props: EmojiRatingProp) => {
  const classes = useStyles();
  return (
    <Button
      id={props.id}
      onClick={props.onClick}
      className={`${classes.emojiContainer} ${
        props.selected ? classes.emojiSelected : classes.emojiDefault
      }`}>
      <Box className={classes.emoji} id={`svg-container-${props.id}`}>
        {props.emoji}
      </Box>
      <Typography
        id={`text-container-${props.id}`}
        variant="body2"
        textAlign="center"
        fontSize="14px"
        lineHeight="14px">
        {props.text}
      </Typography>
    </Button>
  );
};
